import type { AutomationsPresentationV1Node } from 'Automations/automationEditor/types'
import {
  type ConductorTask,
  createSimpleTask,
  createSwitchTask,
  getTaskReferenceName,
  wrapVariables
} from 'Automations/nodeDefinitions/conductor'
import type {
  ConditionSupportingTask,
  ConditionValueSupportingTask
} from 'Automations/nodeDefinitions/conditions'
import {
  isQuoteComparisonSubOperator,
  readPreviousApprovalValue,
  scopedSemanticTypeKey
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'
import type { AssignmentRules } from '../../../../review/types'
import {
  type SemanticType,
  semanticTypes
} from 'Automations/nodeDefinitions/semanticTypes'
import {
  isQuoteComparisonField,
  type QuoteComparisonField
} from '../quoteComparison'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

const taskReferenceBuilder = getTaskReferenceName<string>('SWITCH')

/** The outcome the fetch reports when the review step approved the quote. */
const APPROVED_OUTCOME = 'APPROVED'

/**
 * Fields whose value comes straight off the approved quote: the semantic type
 * it is published under, and the field on that quote. Pricing is absent because
 * an executor compares it — see `pricingComparisonBranch`.
 */
const APPROVED_FIELD_BY_OPERATOR: Record<
  Exclude<QuoteComparisonField, 'PRICING'>,
  { semanticType: SemanticType; quoteField: string }
> = {
  ATTACHMENTS: {
    semanticType: semanticTypes.sequence.previousQuoteApproval.attachments,
    quoteField: 'attachmentAssets'
  },
  RAW_TEXT: {
    semanticType: semanticTypes.sequence.previousQuoteApproval.rawText,
    quoteField: 'rawText'
  },
  PAYMENT_TERMS: {
    semanticType: semanticTypes.sequence.previousQuoteApproval.paymentTerms,
    quoteField: 'defaultDueDateDays'
  }
}

/**
 * Fetches the last approved version of the quote, from the review step the row
 * picked. The executor republishes the approval task's input, which is where
 * the quote itself sits:
 *
 *   output.outcome     -> APPROVED | REJECTED
 *   output.quote       -> the review task's input
 *   output.quote.quote -> the quote as it was approved
 *
 * A quote never approved yields an empty output, which the row's gate reads as
 * false.
 */
export const previousApprovalSupportingTask: ConditionValueSupportingTask = ({
  conditionNodeId,
  operator,
  value,
  inputMapping,
  nodes
}) => {
  const { reviewNodeId, operator: subOperator } =
    readPreviousApprovalValue(value)

  // A half-configured row emits no task — its expression is a plain `false`, so
  // the fetch would answer nothing. Generation runs on every save and a throw
  // there is swallowed for an inert placeholder workflow; validation surfaces
  // the missing pieces in the editor instead.
  if (!reviewNodeId) return undefined
  if (!isQuoteComparisonField(operator)) return undefined
  if (!isQuoteComparisonSubOperator(subOperator)) return undefined

  // The chosen step's approvers are what the executor matches a past approval
  // on. A row pointing at a step the graph no longer reaches emits nothing
  // rather than a fetch that could match the wrong approval.
  const approverUserIds = approversOnReviewNode({
    nodes,
    reviewNodeId
  })
  if (!approverUserIds) return undefined

  // Named after the subject — the review step — never the condition row, so
  // rows sharing a step share one fetch, and rows on different steps don't
  // collide on a name while needing different assignment rules.
  const taskReferenceName = taskReferenceBuilder(conditionNodeId)
  const fetchReference = taskReferenceName(
    `getPreviousQuoteApproval_${reviewNodeId}`
  )

  const fetchApproval = createSimpleTask({
    name: 'get_sequence_data',
    taskReferenceName: fetchReference,
    inputParameters: {
      taskExecutorConfiguration: {
        executor: TASK_EXECUTORS.previousQuoteApproval,
        version: 1
      },
      data: {
        ...wrapVariables({ quoteId: 'workflow.input.quoteId' }),
        // Matched as a whole against what the review step stored, so this has
        // to be the object `reviewQuote` builds from the same approvers.
        assignmentRules: {
          type: 'users',
          users: approverUserIds
        } satisfies AssignmentRules
      }
    }
  })

  const outcomeMapping = {
    [scopedSemanticTypeKey({
      semanticType: semanticTypes.sequence.previousQuoteApproval.outcome,
      scope: reviewNodeId
    })]: `${fetchReference}.output.outcome`
  }

  if (operator === 'PRICING') {
    return pricingComparisonBranch({
      taskReferenceName,
      fetchApproval,
      fetchReference,
      reviewNodeId,
      outcomeMapping,
      quotePhases: inputMapping[semanticTypes.sequence.quote.phases]
    })
  }

  const field = APPROVED_FIELD_BY_OPERATOR[operator]

  return {
    tasks: [fetchApproval],
    semanticTypeMapping: {
      ...outcomeMapping,
      [scopedSemanticTypeKey({
        semanticType: field.semanticType,
        scope: reviewNodeId
      })]: `${approvedQuotePath(fetchReference)}.${field.quoteField}`
    }
  }
}

/**
 * Pricing is compared by the `compare_quote_pricing` executor, which needs
 * phases on both sides. A quote with no approval has none, so the comparison
 * sits inside a gate rather than running unconditionally.
 *
 *   getPreviousQuoteApproval_<step>
 *     -> previousApprovalOutcome_<step>
 *          +-- APPROVED -> compareQuotePricing_<step>
 *          +-- default  -> nothing
 */
const pricingComparisonBranch = ({
  taskReferenceName,
  fetchApproval,
  fetchReference,
  reviewNodeId,
  outcomeMapping,
  quotePhases
}: {
  taskReferenceName: (name: string) => string
  fetchApproval: ConductorTask
  fetchReference: string
  reviewNodeId: string
  outcomeMapping: Record<string, string>
  quotePhases: string | undefined
}): ConditionSupportingTask | undefined => {
  // Without the quote's phases there is nothing to compare against, so emit no
  // task rather than one holding an input that will never resolve.
  if (!quotePhases) return undefined

  const compareReference = taskReferenceName(
    `compareQuotePricing_${reviewNodeId}`
  )
  const gateReference = taskReferenceName(
    `previousApprovalOutcome_${reviewNodeId}`
  )

  const compareQuotePricing = createSimpleTask({
    name: 'get_sequence_data',
    taskReferenceName: compareReference,
    inputParameters: {
      taskExecutorConfiguration: {
        executor: TASK_EXECUTORS.compareQuotePricing,
        version: 1
      },
      // `source` is the quote under review, `target` the approved version it is
      // measured against — the order the executor reports differences in.
      data: wrapVariables({
        source: quotePhases,
        target: `${approvedQuotePath(fetchReference)}.phases`
      })
    }
  })

  return {
    tasks: [
      fetchApproval,
      createSwitchTask({
        name: 'previous_approval_outcome',
        taskReferenceName: gateReference,
        inputParameters: wrapVariables({
          outcome: `${fetchReference}.output.outcome`
        }),
        evaluatorType: 'value-param',
        expression: 'outcome',
        decisionCases: { [APPROVED_OUTCOME]: [compareQuotePricing] },
        defaultCase: []
      })
    ],
    // One comparison answers every pricing question, so publish each outcome
    // rather than branching on this row's comparison — rows on one step share
    // the branch, and `pruneUnusedInputs` drops unreferenced keys.
    semanticTypeMapping: Object.entries(
      semanticTypes.sequence.quotePricingComparison
    ).reduce<Record<string, string>>(
      (mapping, [outcome, semanticType]) => ({
        ...mapping,
        // The executor's output is flat — `valueToTree` with no wrapper key,
        // unlike the get-quote executor's `output.quote`.
        [scopedSemanticTypeKey({
          semanticType,
          scope: reviewNodeId
        })]: `${compareReference}.output.${outcome}`
      }),
      outcomeMapping
    )
  }
}

/**
 * The approvers configured on the chosen review step, or `undefined` when the
 * graph no longer reaches it — a step the user deleted or retyped after the row
 * was stored.
 */
const approversOnReviewNode = ({
  nodes,
  reviewNodeId
}: {
  nodes: AutomationsPresentationV1Node[]
  reviewNodeId: string
}): Array<string> | undefined => {
  const reviewNode = nodes.find(
    node => node.type === 'REVIEW_QUOTE' && node.id === reviewNodeId
  )
  if (!reviewNode) return undefined

  const { approverUserIds } = reviewNode.data as { approverUserIds?: string[] }
  return approverUserIds?.length ? approverUserIds : undefined
}

const approvedQuotePath = (fetchReference: string): string =>
  `${fetchReference}.output.quote.quote`
